import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    model,
    output,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25IconComponent } from "../../../s25-icon/s25.icon.component";
import { Item } from "../../../../pojo/Item";
import { S25DatepickerModule } from "../../../s25-datepicker/s25.datepicker.module";
import { UserStore } from "../../../../stores/user.store";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";
import { S25DropdownPaginatedModule } from "../../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../../pojo/DropDownItem";
import { S25ItemI } from "../../../../pojo/S25ItemI";
import { SeObjectSearchStore } from "./se.object.search.store";
import { S25TriggerRerenderModule } from "../../../s25-trigger-rerender/s25.trigger.rerender.module";

/**
 * Shared filter state for Student Engagement object search (events and organizations).
 * When adding or changing a filter field, follow `.cursor/skills/se-object-search-filters/SKILL.md`
 * and update the query builders in `se.object.search.util.ts` (including `buildEventSearchModel`).
 */
export interface ObjectSearchFilter {
    searchTerm: string;
    timeRange: "upcoming" | "past" | "today" | "week" | "month" | "all" | "custom";
    eventType: "all" | "attending" | "promoted" | "following" | "organizing";
    organizations?: DropDownItem<number>[];
    roles: DropDownItem<number>[] | undefined;
    categories: DropDownItem<number>[] | undefined;
    types: DropDownItem<number>[] | undefined;
    statuses: DropDownItem<number>[] | undefined;
    startDate: string | number | Date | undefined | null;
    endDate: string | number | Date | undefined | null;
}

export const DEFAULT_SEARCH_FILTER: ObjectSearchFilter = {
    searchTerm: "",
    timeRange: "upcoming",
    eventType: "all",
    organizations: undefined,
    roles: undefined,
    categories: undefined,
    types: undefined,
    statuses: undefined,
    startDate: undefined,
    endDate: undefined,
};

@Component({
    selector: "s25-ng-se-object-search-filter",
    template: `
        <form class="filters-container" (ngSubmit)="onSearchSubmit()">
            <div class="search-wrapper" role="search">
                <label for="object-search-input" class="visually-hidden">{{ searchPlaceholder() }}</label>
                <s25-ng-icon [type]="'search'" [size]="18" class="search-icon" [ariaLabel]="false" />
                <input
                    id="object-search-input"
                    type="search"
                    name="objectSearchTerm"
                    class="search-input"
                    autocomplete="off"
                    [attr.aria-label]="searchPlaceholder()"
                    [placeholder]="searchPlaceholder()"
                    [ngModel]="searchTermDraft()"
                    (ngModelChange)="onSearchTermDraftInput($event)"
                />
                @if (searchTermDraft()) {
                    <button class="clear-btn" (click)="clearSearchTermDraft()" type="button" aria-label="Clear search">
                        <s25-ng-icon [type]="'close'" [size]="14" [ariaLabel]="false" />
                    </button>
                }
            </div>

            <fieldset class="filter-controls">
                <legend class="visually-hidden">Filter options</legend>
                @if (isEventType()) {
                    <select
                        class="filter-select"
                        name="eventType"
                        aria-label="Filter by event type"
                        [ngModel]="filterOptions().eventType || 'all'"
                        (ngModelChange)="onFilterUpdate('eventType', $event)"
                    >
                        <option value="all">All Events</option>
                        <option value="attending">Attending</option>
                        <option value="promoted">Promoted</option>
                        <option value="following">Following</option>
                    </select>

                    <s25-ng-dropdown-multi-search-criteria
                        *s25-ng-trigger-rerender="pickerRemountKey()"
                        class="se-filter-criteria-dropdown"
                        [type]="'studentEngagementOrganizations'"
                        [(value)]="organizationsModel"
                        [placeholder]="'All Organizations'"
                        [useStaticOnly]="!!organizations()?.length"
                        [staticItems]="myEventsOrgDropdownItems()"
                        [hasDone]="false"
                        [ariaLabel]="'Filter by organization'"
                        (done)="onOrganizationsFilterDone($event)"
                    />
                    <select
                        class="filter-select filter-select--time-range"
                        name="timeRange"
                        aria-label="Filter by time range"
                        [ngModel]="filterOptions().timeRange || 'upcoming'"
                        (ngModelChange)="onFilterUpdate('timeRange', $event)"
                    >
                        <option value="upcoming">Upcoming</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="past">Past Events</option>
                        <option value="all">All Time</option>
                        <option value="custom">Custom</option>
                    </select>

                    @if (filterOptions().timeRange === "custom") {
                        <s25-datepicker
                            [value]="filterOptions().startDate"
                            [allowEmpty]="true"
                            [autoSelectToday]="false"
                            [placeholder]="'Start date'"
                            [ariaLabel]="'Filter start date'"
                            (valueChange)="onFilterUpdate('startDate', $event)"
                        />
                        <s25-datepicker
                            [value]="filterOptions().endDate"
                            [allowEmpty]="true"
                            [autoSelectToday]="false"
                            [placeholder]="'End date'"
                            [ariaLabel]="'Filter end date'"
                            (valueChange)="onFilterUpdate('endDate', $event)"
                        />
                    }
                } @else {
                    @if (contactId()) {
                        <s25-ng-dropdown-multi-search-criteria
                            *s25-ng-trigger-rerender="pickerRemountKey()"
                            [type]="'organizationRoles'"
                            [(value)]="rolesModel"
                            [placeholder]="'Roles'"
                            [hasDone]="false"
                            [ariaLabel]="'Filter by role'"
                            (done)="onFilterUpdate('roles', $event)"
                        />
                    }

                    @if (isAdmin()) {
                        <s25-ng-dropdown-multi-search-criteria
                            *s25-ng-trigger-rerender="pickerRemountKey()"
                            [type]="'organizationRatings'"
                            [(value)]="statusesModel"
                            [placeholder]="'Status'"
                            [hasDone]="false"
                            [ariaLabel]="'Filter by organization status'"
                            (done)="onFilterUpdate('statuses', $event)"
                        />
                    }

                    <s25-ng-dropdown-multi-search-criteria
                        *s25-ng-trigger-rerender="pickerRemountKey()"
                        [type]="'organizationCategories'"
                        [(value)]="categoriesModel"
                        [placeholder]="'Categories'"
                        [hasDone]="false"
                        [ariaLabel]="'Filter by organization categories'"
                        (done)="onFilterUpdate('categories', $event)"
                    />

                    <s25-ng-dropdown-multi-search-criteria
                        *s25-ng-trigger-rerender="pickerRemountKey()"
                        [type]="'organizationTypes'"
                        [(value)]="typesModel"
                        [placeholder]="'Types'"
                        [hasDone]="false"
                        [ariaLabel]="'Filter by organization types'"
                        (done)="onFilterUpdate('types', $event)"
                    />
                }

                <button class="aw-button aw-button--primary search-btn" type="submit">Search</button>
                @if (hasActiveFilters()) {
                    <button class="text-btn" (click)="clear()" type="button">Clear filters</button>
                }
            </fieldset>
        </form>

        @if (hasActiveFilters()) {
            <div class="active-filters" role="group" aria-label="Active filters">
                <span class="filter-label" id="active-filters-label">Active filters:</span>
                @if (filterOptions().searchTerm) {
                    <span class="filter-pill">
                        "{{ filterOptions().searchTerm }}"
                        <button
                            (click)="clearAppliedSearchTerm()"
                            type="button"
                            [attr.aria-label]="'Remove search filter: ' + filterOptions().searchTerm"
                        >
                            ×
                        </button>
                    </span>
                }
                @if (isEventType()) {
                    @if (filterOptions().eventType !== "all") {
                        <span class="filter-pill">
                            {{ eventTypeLabel() }}
                            <button
                                (click)="onFilterUpdate('eventType', 'all')"
                                type="button"
                                [attr.aria-label]="'Remove event type filter: ' + eventTypeLabel()"
                            >
                                ×
                            </button>
                        </span>
                    }
                    @if (filterOptions().timeRange !== "upcoming") {
                        <span class="filter-pill">
                            {{ timeRangeLabel() }}
                            <button
                                (click)="onFilterUpdate('timeRange', 'upcoming')"
                                type="button"
                                [attr.aria-label]="'Remove time range filter: ' + timeRangeLabel()"
                            >
                                ×
                            </button>
                        </span>
                    }
                    @for (organization of filterOptions().organizations; track organization.itemId) {
                        <span class="filter-pill">
                            {{ organization.itemName }}
                            <button
                                (click)="removeOrganization(organization)"
                                type="button"
                                [attr.aria-label]="'Remove organization filter: ' + organization.itemName"
                            >
                                ×
                            </button>
                        </span>
                    }
                } @else {
                    @for (role of filterOptions().roles; track role.itemId) {
                        <span class="filter-pill">
                            {{ role.itemName }}
                            <button
                                (click)="removeFromArray('roles', role)"
                                type="button"
                                [attr.aria-label]="'Remove role filter: ' + role.itemName"
                            >
                                ×
                            </button>
                        </span>
                    }
                    @for (status of filterOptions().statuses; track status.itemId) {
                        <span class="filter-pill">
                            {{ status.itemName }}
                            <button
                                (click)="removeFromArray('statuses', status)"
                                type="button"
                                [attr.aria-label]="'Remove status filter: ' + status.itemName"
                            >
                                ×
                            </button>
                        </span>
                    }
                    @for (category of filterOptions().categories; track category.itemId) {
                        <span class="filter-pill">
                            {{ category.itemName }}
                            <button
                                (click)="removeFromArray('categories', category)"
                                type="button"
                                [attr.aria-label]="'Remove category filter: ' + category.itemName"
                            >
                                ×
                            </button>
                        </span>
                    }
                    @for (orgType of filterOptions().types; track orgType.itemId) {
                        <span class="filter-pill">
                            {{ orgType.itemName }}
                            <button
                                (click)="removeFromArray('types', orgType)"
                                type="button"
                                [attr.aria-label]="'Remove type filter: ' + orgType.itemName"
                            >
                                ×
                            </button>
                        </span>
                    }
                }
            </div>
        }
    `,
    styles: `
        .filters-container {
            display: flex;
            gap: 1rem;
            margin-bottom: 1.5rem;
            flex-wrap: wrap;
        }

        .visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }

        .search-wrapper {
            flex: 1;
            min-width: 240px;
            position: relative;
        }

        .search-icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--color--text-muted, #6b7280);
            pointer-events: none;
        }

        .search-input {
            width: 100%;
            padding: 0.5rem 2rem 0.5rem 2.5rem;
            border: 1px solid var(--color--border-bold, #b3b3b3);
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color var(--transition-medium, 200ms) ease;
            background: var(--color--form-bg, white);
            color: var(--color--text, #333);
        }

        .search-input:focus {
            outline: none;
            border-color: var(--color--border-bold-hover, #666);
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        .search-input::placeholder {
            color: var(--color--text-muted, #666);
        }

        .search-input::-webkit-search-cancel-button {
            -webkit-appearance: none;
            appearance: none;
        }

        .clear-btn {
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            cursor: pointer;
            padding: 0.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: var(--border-radius, 3px);
            transition: all var(--transition-medium, 200ms) ease;
        }

        .clear-btn:hover {
            color: var(--color--text, #333);
            background: var(--color--surface-2-bg, #f0f0f0);
        }

        .clear-btn:focus {
            outline: none;
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        .filter-controls {
            display: flex;
            gap: 0.75rem;
            align-items: flex-start;
            border: none;
            padding: 0;
            margin: 0;
        }

        .filter-select {
            padding: 0.5rem 2rem 0.5rem 0.75rem;
            border: 1px solid var(--color--border-bold, #b3b3b3);
            border-radius: 6px;
            font-size: 0.875rem;
            cursor: pointer;
            transition: border-color var(--transition-medium, 200ms) ease;
            appearance: none;
            background: var(--color--form-bg, white)
                url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")
                no-repeat right 0.5rem center;
            background-size: 16px;
            min-width: 140px;
            color: var(--color--text, #333);
        }

        .filter-select:focus {
            outline: none;
            border-color: var(--color--border-bold-hover, #666);
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        .filter-select--time-range {
            min-width: 11rem;
            flex-shrink: 0;
        }

        :host ::ng-deep .filter-controls s25-datepicker .c-input {
            width: 100%;
            padding: 0.5rem 0.75rem;
            border: 1px solid #e5e7eb !important;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color 0.2s ease;
            background: white;
            height: unset;
        }

        :host ::ng-deep .filter-controls .se-filter-criteria-dropdown .ngDropdownPaginated,
        :host ::ng-deep .filter-controls .ngDropdownPaginated {
            border: 1px solid var(--color--border-bold, #b3b3b3) !important;
            border-radius: 6px;
            min-width: 140px;
        }

        :host ::ng-deep .filter-controls .ngDropdownPaginated .select2-container {
            font-size: 0.875rem;
        }

        :host ::ng-deep .filter-controls .ngDropdownPaginated .select2-container .select2-choice.ui-select-match {
            padding: 0.5rem 2em 0.5rem 5px;
            display: flex;
            height: unset !important;
            line-height: unset;
        }

        :host
            ::ng-deep
            .filter-controls
            .ngDropdownPaginated
            .select2-container
            .select2-choice.ui-select-match::after {
            right: 4px;
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
            width: 17px;
        }

        :host ::ng-deep .filter-controls .ngDropdownPaginated .select2-container .ngDropdownDrop {
            left: 50%;
            transform: translateX(-50%);
            min-width: 11em;
        }

        :host
            ::ng-deep
            .filter-controls
            .ngDropdownPaginated
            .select2-container
            .select2-choice.ui-select-match
            .ngDropdownContent {
            color: var(--color--text);
            min-height: unset;
        }

        .text-btn {
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            font-size: 0.875rem;
            cursor: pointer;
            padding: 0.5rem 0.75rem;
            border-radius: 6px;
            transition: all var(--transition-medium, 200ms) ease;
        }

        .text-btn:hover {
            color: var(--color--text, #333);
            background: var(--color--surface-2-bg, #f0f0f0);
        }

        .search-btn {
            white-space: nowrap;
            align-self: flex-start;
        }

        .text-btn:focus {
            outline: none;
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        .active-filters {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            margin-bottom: 1rem;
            font-size: 0.875rem;
            flex-wrap: wrap;
        }

        .filter-label {
            color: var(--color--text-muted, #666);
        }

        .filter-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.25rem 0.625rem;
            background: var(--color--surface-2-bg, #f0f0f0);
            border-radius: var(--border-radius, 3px);
            font-size: 0.8125rem;
            color: var(--color--text, #333);
        }

        .filter-pill button {
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            cursor: pointer;
            padding: 0.125rem;
            font-size: 1.125rem;
            line-height: 1;
            display: flex;
            align-items: center;
            border-radius: var(--border-radius--inner, 2.5px);
            transition: color var(--transition-medium, 200ms) ease;
        }

        .filter-pill button:hover {
            color: var(--color--text, #333);
        }

        .filter-pill button:focus {
            outline: none;
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        @media (max-width: 768px) {
            .filters-container {
                flex-direction: column;
            }

            .filter-controls {
                flex-direction: column;
                align-items: stretch;
                width: 100%;
            }

            .filter-select {
                width: 100%;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25IconComponent, S25DatepickerModule, S25DropdownPaginatedModule, S25TriggerRerenderModule],
})
export class SeObjectSearchFilterComponent {
    itemTypeId = input.required<Partial<Item.Ids>>();
    filterOptions = model.required<ObjectSearchFilter>();
    organizations = input<S25ItemI[]>([]);

    readonly searchClick = output<void>();

    /** Text in the search box; applied to filter options on Enter or Search only. */
    readonly searchTermDraft = linkedSignal(() => this.filterOptions().searchTerm);

    protected readonly ItemIds = Item.Ids;

    private readonly userStore = inject(UserStore);
    private readonly searchStore = inject(SeObjectSearchStore);

    readonly isAdmin = computed(() => this.searchStore.isAdmin());
    readonly contactId = computed(() => this.searchStore.contactIdParam());

    protected readonly isEventType = computed(() => this.itemTypeId() === Item.Ids.Event);

    protected rolesModel = linkedSignal(() => this.filterOptions().roles ?? []);
    protected statusesModel = linkedSignal(() => this.filterOptions().statuses ?? []);
    protected categoriesModel = linkedSignal(() => this.filterOptions().categories ?? []);
    protected typesModel = linkedSignal(() => this.filterOptions().types ?? []);
    protected organizationsModel = linkedSignal(() => this.filterOptions().organizations ?? []);

    /** Incremented on clear() to remount multiselect dropdowns and reset their display state. */
    protected readonly pickerRemountKey = signal(0);

    protected readonly myEventsOrgDropdownItems = computed((): DropDownItem[] =>
        (this.organizations() ?? []).map((organization) => ({
            itemId: organization.itemId,
            itemName: organization.itemName ?? organization.txt,
            itemTypeId: 2,
        })),
    );

    readonly hasActiveFilters = computed(() => {
        const f = this.filterOptions();
        if (this.isEventType()) {
            return (
                f.eventType !== "all" || f.timeRange !== "upcoming" || f.searchTerm !== "" || !!f.organizations?.length
            );
        }
        return (
            !!f.types?.length ||
            !!f.categories?.length ||
            !!f.roles?.length ||
            !!f.statuses?.length ||
            f.searchTerm !== ""
        );
    });

    protected readonly searchPlaceholder = computed(() =>
        this.isEventType() ? "Search events..." : "Search organizations...",
    );

    protected readonly eventTypeLabel = computed(() => {
        const labels: Record<string, string> = {
            all: "All Events",
            attending: "Attending",
            promoted: "Promoted",
            following: "Following",
        };
        return labels[this.filterOptions().eventType] ?? "All Events";
    });

    protected readonly timeRangeLabel = computed(() => {
        const options = this.filterOptions();
        const labels: Record<string, string> = {
            upcoming: "Upcoming",
            past: "Past",
            today: "Today",
            week: "This Week",
            month: "This Month",
            all: "All Time",
            custom: `${S25Datefilter.transform(options.startDate, this.userStore.dateFormat()!) ?? "No Start Date Selected"} - ${S25Datefilter.transform(options.endDate, this.userStore.dateFormat()!) ?? "No End Date Selected"}`,
        };
        return labels[options.timeRange] ?? "Upcoming";
    });

    protected onSearchTermDraftInput(value: string): void {
        this.searchTermDraft.set(value);
    }

    protected clearSearchTermDraft(): void {
        this.searchTermDraft.set("");
    }

    protected clearAppliedSearchTerm(): void {
        this.onFilterUpdate("searchTerm", "");
        this.searchTermDraft.set("");
    }

    protected onSearchSubmit(): void {
        this.commitSearchTerm();
        this.searchClick.emit();
    }

    private commitSearchTerm(): void {
        const searchTerm = this.searchTermDraft();
        if (this.filterOptions().searchTerm === searchTerm) {
            return;
        }
        this.onFilterUpdate("searchTerm", searchTerm);
    }

    onFilterUpdate<K extends keyof ObjectSearchFilter>(prop: K, value: ObjectSearchFilter[K]) {
        this.filterOptions.update((f) => ({ ...f, [prop]: value }));
    }

    onOrganizationsFilterDone(selectedOrganizations: DropDownItem<number>[] | null | undefined) {
        const organizations = selectedOrganizations?.length ? selectedOrganizations : undefined;
        this.onFilterUpdate("organizations", organizations);
        this.organizationsModel.set(selectedOrganizations ?? []);
    }

    removeOrganization(organization: DropDownItem) {
        const current = this.filterOptions().organizations ?? [];
        const updated = current.filter((item) => item.itemId !== organization.itemId);
        this.onFilterUpdate("organizations", updated.length ? updated : undefined);
        this.organizationsModel.set(updated);
    }

    removeFromArray(prop: "roles" | "statuses" | "categories" | "types", item: DropDownItem) {
        const current = this.filterOptions()[prop] ?? [];
        const updated = current.filter((i) => i.itemId !== item.itemId);
        this.onFilterUpdate(prop, updated.length ? updated : undefined);
        switch (prop) {
            case "roles":
                this.rolesModel.set(updated);
                break;
            case "statuses":
                this.statusesModel.set(updated);
                break;
            case "categories":
                this.categoriesModel.set(updated);
                break;
            case "types":
                this.typesModel.set(updated);
                break;
        }
    }

    clear() {
        this.filterOptions.set({ ...DEFAULT_SEARCH_FILTER });
        this.searchTermDraft.set("");
        this.rolesModel.set([]);
        this.statusesModel.set([]);
        this.categoriesModel.set([]);
        this.typesModel.set([]);
        this.organizationsModel.set([]);
        this.pickerRemountKey.update((key) => key + 1);
    }
}
